import React from 'react';
import { cloneDeep } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Card,
    CardHeader,
    CardContent,
    Avatar,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPencil } from '@fortawesome/pro-light-svg-icons';
import {
    UPDATE_RULE_SET, UPDATE_CURRENT_RULE_SET_IN_LIST, RESET_CURRENT_AWARDING_RULE, UPDATE_CURRENT_AWARDING_RULE,
    UPDATE_CURRENT_CUSTOM_DATA_FIELD_INFOS, UPDATE_CURRENT_HYPERFIND_BELONGING_INFO, UPDATE_CURRENT_DATAVIEW_INFOS,
} from '../../../store/ruleSet';
import { Order, DataType, AwardingRuleParameterType, } from '../../../../Enums';
import SortingRuleEdit from './SortingRuleEdit';
import SortingRulesSummary from './SortingRulesSummary';
import './Step2-SortingRuleSection.scss';

/**
 * @type {ApiAdmin.IBodyAwardingRules}
 */
export const DEFAULT_AWARDING_RULE_PARAMETER = {
    type: -1,
    order: -1,
    index: -1,
    customDataFieldInfos: null,
    hyperfindBelongingInfos: null,
    dataviewInfos: null,
};

const defaultValidationErrors = {
    customDataFieldId: false,
    customDateFormat: false,
    dataType: false,
    dataviewId: false,
    dataviewKeyAlias: false,
    hyperfindId: false,
    order: false,
    type: false,
};

/**
 * @param {{
 *  validationErrors: {[Key: string], isDisabled : boolean },
 *  isDisabled: boolean,
 *  stepNumber: number,
 * }} props
 */
export default function SortingRuleSection(props) {
    const { validationErrors, isDisabled, stepNumber } = props;
    const dispatch = useDispatch();

    /** @type {EmploymentBillboard.AdminStore} */
    // @ts-ignore
    const { adminRuleSet, adminAppData } = useSelector((state) => state);
    const {
        currentAwardingRule,
        awardingRules,
    } = adminRuleSet;

    /** @type {[boolean, Function]} */
    const [isEditting, setIsEditting] = React.useState(false);

    /** @type {[ApiAdmin.IBodyAwardingRules, Function]} */
    const [fieldValidationErrors, setFieldValidationErrors] = React.useState(cloneDeep(defaultValidationErrors));

    const validateCurrentAwardingRule = () => {
        let isValid = true;
        const errors = cloneDeep(defaultValidationErrors);

        if (currentAwardingRule.type === -1) {
            errors.type = true;
            isValid = false;
        }
        if (currentAwardingRule.order === -1) {
            errors.order = true;
            isValid = false;
        }
        switch (currentAwardingRule.type) {
            case AwardingRuleParameterType.CustomDataType: {
                if (currentAwardingRule.customDataFieldInfos.customDataFieldId === null) {
                    errors.customDataFieldId = true;
                    isValid = false;
                }
                if (currentAwardingRule.customDataFieldInfos.dataType === null) {
                    errors.dataType = true;
                    isValid = false;
                } else if (currentAwardingRule.customDataFieldInfos.dataType === DataType.Date && currentAwardingRule.customDataFieldInfos.customDateFormat === null) {
                    errors.customDateFormat = true;
                    isValid = false;
                }
                break;
            }
            case AwardingRuleParameterType.HyperfindBelonging: {
                if (currentAwardingRule.hyperfindBelongingInfos.hyperfindId === null) {
                    errors.hyperfindId = true;
                    isValid = false;
                }
                break;
            }
            case AwardingRuleParameterType.DataviewData: {
                if (currentAwardingRule.dataviewInfos.dataviewId === null) {
                    errors.dataviewId = true;
                    isValid = false;
                }
                if (currentAwardingRule.dataviewInfos.dataviewKeyAlias === "") {
                    errors.dataviewKeyAlias = true;
                    isValid = false;
                }
                if (currentAwardingRule.dataviewInfos.dataType === null) {
                    errors.dataType = true;
                    isValid = false;
                } else if (currentAwardingRule.dataviewInfos.dataType === DataType.Date && currentAwardingRule.dataviewInfos.customDateFormat === null) {
                    errors.customDateFormat = true;
                    isValid = false;
                }
                break;
            }
            default:
                break;
        }

        if (errors.type !== fieldValidationErrors.type || errors.order !== fieldValidationErrors.order || errors.customDataFieldId !== fieldValidationErrors.customDataFieldId
            || errors.customDateFormat !== fieldValidationErrors.customDateFormat || errors.dataType !== fieldValidationErrors.dataType || errors.hyperfindId !== fieldValidationErrors.hyperfindId
            || errors.dataviewId !== fieldValidationErrors.dataviewId || errors.dataviewKeyAlias !== fieldValidationErrors.dataviewKeyAlias) {
            setFieldValidationErrors(errors);
        }

        return isValid;
    }

    /**
     * @param {string} target
     * @param { number | boolean } value
     */
    const updateRuleSetStore = (target, value) => {
        dispatch({
            type: UPDATE_RULE_SET,
            payload: {
                [target]: value,
            },
        });
    };

    const updateCurrentAwardingRule = (target, value) => {
        dispatch({
            type: UPDATE_CURRENT_AWARDING_RULE,
            payload: {
                [target]: value
            }
        });
    }

    const resetValidationErrors = () => setFieldValidationErrors(defaultValidationErrors);

    const resetCurrentAwardingRule = () => {
        dispatch({ type: RESET_CURRENT_AWARDING_RULE });
        resetValidationErrors();
    }

    /**
     * @param {number} index
     * @param {number} value
     */
    const handleSourceOptionSelected = (index, value) => {
        const clearedCurrentAwardingRule = cloneDeep(DEFAULT_AWARDING_RULE_PARAMETER);
        clearedCurrentAwardingRule.type = value;
        if (isEditting) {
            clearedCurrentAwardingRule.index = index;
        }
        switch (value) {
            case AwardingRuleParameterType.CustomDataType:
                clearedCurrentAwardingRule.customDataFieldInfos = {
                    customDataFieldId: null,
                    customDateFormat: null,
                    dataType: null,
                }
                break;
            case AwardingRuleParameterType.HyperfindBelonging:
                clearedCurrentAwardingRule.hyperfindBelongingInfos = {
                    hyperfindId: null,
                }
                break;
            case AwardingRuleParameterType.DataviewData:
                clearedCurrentAwardingRule.dataviewInfos = {
                    dataviewId: null,
                    dataviewKeyAlias: '',
                    dataType: null,
                    customDateFormat: null,
                }
                break;
            case AwardingRuleParameterType.BySubmittedRequestDateTime:
                clearedCurrentAwardingRule.order = Order.Ascending;
                break;
            default:
                break;
        }

        updateRuleSetStore('currentAwardingRule', clearedCurrentAwardingRule);
        resetValidationErrors();
    };

    /**
     * @param {string} target
     * @param { number | boolean } value
     */
    const handleCustomDataFieldInfosChange = (target, value) => {
        if (target === 'order') {
            updateCurrentAwardingRule(target, value);
            return;
        }
        else if (target === 'dataType') {
            const updatedParameter = {};

            if (value.type === DataType.Numeric) {
                updatedParameter.dataType = DataType.Numeric;
                updatedParameter.customDateFormat = null;
            } else {
                updatedParameter[target] = DataType.Date;
                updatedParameter.customDateFormat = value.value;
                updatedParameter.dataType = value.type;
            }

            dispatch({
                type: UPDATE_CURRENT_CUSTOM_DATA_FIELD_INFOS,
                payload: {
                    ...updatedParameter
                },
            });
            return;
        }
        dispatch({
            type: UPDATE_CURRENT_CUSTOM_DATA_FIELD_INFOS,
            payload: {
                [target]: value,
            },
        });
    }
    /**
     * @param {string} target
     * @param { number | boolean } value
     */
    const handleHyperfindBelongingInfosChange = (target, value) => {
        if (target === 'order') {
            updateCurrentAwardingRule(target, value);
            return;
        }
        dispatch({
            type: UPDATE_CURRENT_HYPERFIND_BELONGING_INFO,
            payload: {
                [target]: value,
            },
        });
    }
    /**
     * @param {string} target
     * @param { number | boolean } value
     */
    const handleDataviewInfosChange = (target, value) => {
        if (target === 'order') {
            updateCurrentAwardingRule(target, value);
            return;
        }
        else if (target === 'dataType') {
            const updatedParameter = {};
            if (value.type === DataType.Numeric) {
                updatedParameter.dataType = DataType.Numeric;
                updatedParameter.customDateFormat = null;
            } else {
                updatedParameter[target] = DataType.Date;
                updatedParameter.customDateFormat = value.value;
                updatedParameter.dataType = value.type;
            }
            dispatch({
                type: UPDATE_CURRENT_DATAVIEW_INFOS,
                payload: {
                    ...updatedParameter
                },
            });
            return;
        }
        else if(target === 'dataviewId'){
            dispatch({
                type: UPDATE_CURRENT_DATAVIEW_INFOS,
                payload: {
                    [target]: value,
                    dataviewKeyAlias: '',
                },
            });
            return;
        }
        dispatch({
            type: UPDATE_CURRENT_DATAVIEW_INFOS,
            payload: {
                [target]: value,
            },
        });
    }

    const handleAddParameter = () => {
        if (validateCurrentAwardingRule()) {
            if (isEditting) {
                dispatch({
                    type: UPDATE_CURRENT_RULE_SET_IN_LIST,
                    payload: {
                        currentAwardingRule: currentAwardingRule,
                        index: currentAwardingRule.index,
                    },
                });
            }
            else {
                currentAwardingRule.index = awardingRules.length;
                const updatedParameters = [...awardingRules, currentAwardingRule];
                updateRuleSetStore('awardingRules', updatedParameters);
            }
            resetCurrentAwardingRule();
            setIsEditting(false);
        }
    };

    /**
     * @param {number} indexToMove
     * @param {number} direction
     */
    const handleMove = (indexToMove, direction) => {
        [awardingRules[indexToMove]] = awardingRules.splice(indexToMove + direction, 1, awardingRules[indexToMove]);
        awardingRules.forEach((x, index) => x.index = index);
        updateRuleSetStore('awardingRules', awardingRules)
    };
    const handleEditParameter = (index) => {
        updateRuleSetStore('currentAwardingRule', awardingRules[index]);
        setIsEditting(true);
    };

    const handleDeleteParameter = (index) => {
        awardingRules.splice(index, 1);
        awardingRules.forEach((x, index) => x.index = index);
        updateRuleSetStore('awardingRules', awardingRules);
    };

    return (
        <Card className="card">
            <CardHeader
                className="card--header"
                gutterbottom
                avatar={<Avatar className="avatar">{stepNumber}</Avatar>}
                title="Sorting rule"
            />
            <CardContent>
                <SortingRuleEdit
                    currentAwardingRule={currentAwardingRule}
                    handleSourceSelected={handleSourceOptionSelected}
                    handleCustomDataFieldInfosChange={handleCustomDataFieldInfosChange}
                    handleHyperfindBelongingInfosChange={handleHyperfindBelongingInfosChange}
                    handleDataviewInfosChange={handleDataviewInfosChange}
                    isDisabled={isDisabled}
                    fieldValidationErrors={fieldValidationErrors}
                />
                {
                    validationErrors.awardingRules ? (
                        <span style={{ color: 'red', display: 'block' }}>Please select at least one sorting rule</span>) : null
                }
                <div className="srs--buttons-container">
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={resetCurrentAwardingRule}
                        disabled={isDisabled}
                        className="srs--button"
                    >
                        Clear
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleAddParameter}
                        disabled={isDisabled}
                        className="srs--button"
                    >
                        {isEditting ? (
                            <>
                                <FontAwesomeIcon
                                    className="srs--selection-add-update-btn"
                                    icon={faPencil}
                                />
                                Update sort
                            </>) : (
                            <>
                                <FontAwesomeIcon
                                    className="srs--selection-add-update-btn"
                                    icon={faPlus}
                                />
                                Add sort
                            </>)}
                    </Button>
                </div>
                {!!awardingRules.length && (
                    <SortingRulesSummary
                        awardingRules={awardingRules}
                        handleMove={handleMove}
                        handleEdit={handleEditParameter}
                        handleDelete={handleDeleteParameter}
                        isDisabled={isDisabled}
                        adminAppData={adminAppData}
                    />)}
            </CardContent>
        </Card>
    );
}

